//Import components
import React, { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import { useDispatch, useSelector } from 'react-redux'
import { getGeofencingZones } from '../../store/slices/geofencingZones/geofencingZonesActions'
import { getVehiclesData } from '../../store/slices/vehicles/vehiclesAction'
import setGeofencingZonesLayer from './actions/setGeofencingZonesLayer'
import setMapMarkers from './actions/updateMarker/setMapMarkers'

//Import styles
import 'mapbox-gl/dist/mapbox-gl.css'
import '../../assets/styles/vehiclesMap.css'
import setVehiclesLayer from './actions/setVehiclesLayer'

// Duck map
export default function VehiclesMap({ setMapRefData }) {
  const { REACT_APP_MAPBOX_PUBLIC_KEY } = process.env
  const { geofencingZonesData, error: getGeofencingZonesDataError } = useSelector(state => state.geofencingZones)
  const { vehiclesData, error: getVehiclesDataError } = useSelector(state => state.vehicles)
  const { mapStyle } = useSelector(state => state.app)
  const { locationsData, locationIndex } = useSelector(state => state.mapLocations)

  const dispatch = useDispatch()
  // Refs mirror the locations redux state so setMapMarkers's popup-close handler
  // (a closure created once) always sees the latest selection.
  const locationsDataRef = useRef(locationsData)
  const locationIndexRef = useRef(locationIndex)
  const mapContainer = useRef()
  const map = useRef()
  const vehiclesLayerDataRef = useRef(JSON.parse(JSON.stringify(vehiclesData))) // Read latest vehicles data from redux
  const geofencingZonesLayersDataRef = useRef(geofencingZonesData) // Read latest vehicles data from redux
  const mapPopupVehiclesMarkersRef = useRef({}) // Html map markers (for popup)
  const closeMarkerPopupCenterTimeoutIdRef = useRef()
  // Tracks the last bucket reference we pushed to each Mapbox source. With the
  // zones slice now keeping per-bucket reference stability, unchanged buckets
  // hit the `===` short-circuit below and we skip the source.setData call —
  // no polygon re-render / flicker when nothing actually moved.
  const lastSyncedZoneBucketsRef = useRef({})
  const updateVehiclesDataTimeoutId = useRef({})
  const updateGeofencingZonesDataTimeoutId = useRef({})
  // Refresh both data sources every 15 minutes. We fetch once on map open and
  // then wait a full 15 min before pulling again — the backend can't handle
  // chatty polling and per-vehicle/zone state doesn't change that often.
  const FIFTEEN_MINUTES_MS = 15 * 60 * 1000
  const updateVehiclesDataTimeoutDelayValue = useRef(FIFTEEN_MINUTES_MS)
  const updateGeofencingZonesDataTimeoutDelayValue = useRef(FIFTEEN_MINUTES_MS)
  // Initialise the map at the currently selected city so we don't fly from a
  // stale default after a page reload restores a non-zero locationIndex.
  const initialCity = locationsData?.[locationIndex] ?? locationsData?.[0]
  const mapLng = useRef(initialCity?.location?.longitude ?? 28.8353)
  const mapLat = useRef(initialCity?.location?.latitude ?? 47.0105)
  const mapZoom = useRef(initialCity?.zoom ?? 12) // 15.44
  const mapPitch = useRef(0) // 37
  const mapBearing = useRef(0)
  const [isStyleLoaded, setIsStyleLoaded] = useState(false)

  mapboxgl.accessToken = REACT_APP_MAPBOX_PUBLIC_KEY

  // Updates the geofencing zones data ref when geofencingZonesData redux state changes
  useEffect(() => {
    geofencingZonesLayersDataRef.current = geofencingZonesData
  }, [geofencingZonesData])

  // Updates the vehicles data ref when vehiclesData redux state changes and create html marker with popup or update existing
  useEffect(() => {
    vehiclesLayerDataRef.current = JSON.parse(JSON.stringify(vehiclesData))
  }, [vehiclesData])

  // Show error during obtaining data
  useEffect(() => {
    if (getGeofencingZonesDataError) {
      clearTimeout(updateGeofencingZonesDataTimeoutId.current) // Clear get zones data interval on error
    }
    if (getVehiclesDataError) {
      clearTimeout(updateVehiclesDataTimeoutId.current) // Clear get vehicles data interval on error
    }
  }, [getGeofencingZonesDataError, getVehiclesDataError])

  // Keep refs in sync with the latest selected city so popup-close fly-to works.
  useEffect(() => {
    locationsDataRef.current = locationsData
    locationIndexRef.current = locationIndex
  }, [locationsData, locationIndex])

  // Get geofencing zones data, run interval for get data again.
  // Each cycle paginates through every page before scheduling the next tick —
  // setTimeout fires only AFTER the previous cycle finishes so concurrent
  // cycles can't trample each other when the fleet/catalogue is large.
  useEffect(() => {
    let cancelled = false

    async function getNewGeofencingZonesData() {
      await dispatch(getGeofencingZones())
      if (cancelled) return
      updateGeofencingZonesDataTimeoutId.current = setTimeout(
        getNewGeofencingZonesData,
        updateGeofencingZonesDataTimeoutDelayValue.current
      )
    }

    getNewGeofencingZonesData()

    return () => {
      cancelled = true
      clearTimeout(updateGeofencingZonesDataTimeoutId.current)
    }
    // eslint-disable-next-line
  }, [])

  // Get vehicles data, same pattern as zones.
  useEffect(() => {
    let cancelled = false

    async function getNewVehiclesData() {
      await dispatch(getVehiclesData())
      if (cancelled) return
      updateVehiclesDataTimeoutId.current = setTimeout(getNewVehiclesData, updateVehiclesDataTimeoutDelayValue.current)
    }

    getNewVehiclesData()

    return () => {
      cancelled = true
      clearTimeout(updateVehiclesDataTimeoutId.current)
    }
    // eslint-disable-next-line
  }, [])

  // Listen to light mode change and update style
  useEffect(() => {
    // Wait for map to initialize and finish of map loading
    if (map.current) {
      map.current.setStyle(`mapbox://styles/mapbox/standard/?lightPreset=${mapStyle}`, { diff: false })
      map.current.on('style.load', setMapConfig)
      map.current.on('style.load', setNewMapLayers) // Triggered when `setStyle` is called
    }

    // Set map style config
    function setMapConfig() {
      map.current.setConfigProperty('basemap', 'lightPreset', `${mapStyle}`)
    }

    return () => {
      if (map.current) {
        map.current.off('style.load', setMapConfig)
        map.current.off('style.load', setNewMapLayers)
      }
    }
  }, [mapStyle])

  // Map initialisation
  useEffect(() => {
    // Initialize map only once
    if (!map.current) {
      // VehiclesMap initialisation
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/standard',
        center: [mapLng.current, mapLat.current],
        zoom: mapZoom.current,
        pitch: mapPitch.current, // pitch in degrees
        bearing: mapBearing.current, // bearing in degrees
        trackResize: true,
        fadeDuration: 0,
      })

      map.current.on('style.load', setMapConfig) // Set map config style (style and etc.)
      map.current.on('style.load', setNewMapLayers) // Initialize map zones layers

      setMapRefData(map.current)
    }

    // Set map style config
    function setMapConfig() {
      map.current.setConfigProperty('basemap', 'lightPreset', `${mapStyle}`)
    }

    // Cleanup function
    return () => {
      if (map.current) {
        map.current.off('style.load', setMapConfig)
        map.current.off('style.load', setNewMapLayers)
      }
    }
  }, [mapStyle, setMapRefData])

  // Push zone updates into the corresponding Mapbox sources. Each bucket is
  // checked independently — if its reference is the same as last time (i.e.
  // the slice's per-bucket equality kept the old reference because nothing
  // changed) we skip setData and the polygons don't re-render.
  useEffect(() => {
    if (!map.current || !isStyleLoaded) return
    const data = geofencingZonesLayersDataRef.current
    if (!data) return

    const buckets = [
      ['grayArea', 'grayAreaSource'],
      ['speedLimitZones', 'speedLimitZonesSource'],
      ['parkingZones', 'parkingZonesSource'],
      ['noParkingZones', 'noParkingZonesSource'],
    ]
    for (const [key, sourceId] of buckets) {
      const bucketData = data[key]
      if (!bucketData) continue
      if (lastSyncedZoneBucketsRef.current[key] === bucketData) continue // unchanged → skip
      const src = map.current.getSource(sourceId)
      if (!src) continue
      src.setData(bucketData)
      lastSyncedZoneBucketsRef.current[key] = bucketData
    }
  }, [geofencingZonesData, isStyleLoaded])

  // Get vehicles data, run interval for get data again
  useEffect(() => {
    // Wait for map to initialize
    if (map.current && isStyleLoaded && vehiclesLayerDataRef.current && vehiclesLayerDataRef.current?.features?.length > 0) {
      setMapMarkers(
        dispatch,
        locationsDataRef,
        locationIndexRef,
        map,
        vehiclesLayerDataRef,
        mapPopupVehiclesMarkersRef,
        closeMarkerPopupCenterTimeoutIdRef
      ) // Update markers layer
    }
  }, [dispatch, vehiclesData, isStyleLoaded])

  // Set new map layers
  function setNewMapLayers() {
    if (!map.current?.getLayer('grayAreaLayer') && !map.current.getLayer('grayAreaOutlineLayer')) {
      setGeofencingZonesLayer(map.current, geofencingZonesLayersDataRef.current, 'grayAreaLayer')
    }
    if (!map.current?.getLayer('speedLimitZonesLayer') && !map.current?.getLayer('speedLimitZonesOutlineLayer')) {
      setGeofencingZonesLayer(map.current, geofencingZonesLayersDataRef.current, 'speedLimitZonesLayer')
    }
    if (!map.current?.getLayer('parkingZonesLayer') && !map.current?.getLayer('parkingZonesOutlineLayer')) {
      setGeofencingZonesLayer(map.current, geofencingZonesLayersDataRef.current, 'parkingZonesLayer')
    }
    if (!map.current?.getLayer('noParkingZonesLayer') && !map.current?.getLayer('noParkingZonesOutlineLayer')) {
      setGeofencingZonesLayer(map.current, geofencingZonesLayersDataRef.current, 'noParkingZonesLayer')
    }

    if (!map.current.getLayer('vehiclesLayer')) {
      setVehiclesLayer(map.current, vehiclesLayerDataRef.current)
    }

    setIsStyleLoaded(true)
  }

  return <div ref={mapContainer} className="map-container" />
}
